import { Box, makeStyles, Typography } from "@material-ui/core";
import * as React from "react";

import { CustomButton } from "@/components/Button";
import { PublicHeader } from "@/components/PublicHeader";
import { useDevice } from "@/hooks/useDevice";
import theme from "@/theme";

const useStyles = makeStyles(() => ({
  root: {
    minHeight: "100vh",
    backgroundColor: theme.palette.semantic["surface/card"],
    [theme.breakpoints.up("md")]: { backgroundColor: theme.palette.semantic["surface/base"] },
  },
  main: {
    width: "100%",
    margin: "0 auto",
    padding: "calc(32px + env(safe-area-inset-top, 0px)) 24px calc(32px + env(safe-area-inset-bottom, 0px))",
    [theme.breakpoints.up("md")]: {
      width: 560,
      padding: "40px 48px",
      margin: "48px auto 64px",
      borderRadius: 16,
      backgroundColor: theme.palette.semantic["surface/card"],
    },
  },
  phases: {
    paddingTop: "calc(20px + env(safe-area-inset-top, 0px))",
    [theme.breakpoints.up("md")]: { paddingTop: 40 },
  },
  hero: { paddingTop: "calc(48px + env(safe-area-inset-top, 0px))", [theme.breakpoints.up("md")]: { paddingTop: 48 } },
  centered: { minHeight: "80vh", display: "flex", flexDirection: "column", justifyContent: "center" },
  phaseRow: { marginBottom: 24 },
  heading: {
    fontSize: 16,
    fontWeight: 700,
    lineHeight: 1.3,
    whiteSpace: "pre-line",
    textAlign: "center",
    color: theme.palette.semantic["text/primary"],
    [theme.breakpoints.up("md")]: { fontSize: 20 },
  },
  description: {
    fontSize: 14,
    lineHeight: 1.5,
    textAlign: "center",
    color: theme.palette.text.secondary,
    marginTop: 8,
  },
  smallDescription: { fontSize: 12, whiteSpace: "pre-line", [theme.breakpoints.up("md")]: { fontSize: 14 } },
  body: { marginTop: 24 },
  actions: {
    width: "100%",
    margin: "32px auto 0",
    [theme.breakpoints.up("md")]: { maxWidth: 360 },
    "& button": { maxWidth: "100%" },
  },
  back: { marginTop: 4 },
}));

/** 登録フロー専用。診断結果や設定等の既存RegistrationTemplateには影響しない。 */
export const RegistrationFlowLayout: React.FC<{
  children: React.ReactNode;
  phaseTags?: React.ReactNode;
  title?: React.ReactNode;
  description?: React.ReactNode;
  descriptionSize?: "body" | "small";
  hero?: boolean;
  centered?: boolean;
  totalSteps?: number;
  currentStep?: number;
  mainClassName?: string;
  bodyClassName?: string;
}> = ({ children, phaseTags, title, description, descriptionSize = "body", hero, centered, mainClassName, bodyClassName }) => {
  const classes = useStyles();
  const { isMobile } = useDevice();
  return (
    <Box className={classes.root} data-registration-flow>
      {!isMobile && <PublicHeader />}
      <Box
        component="main"
        className={`${classes.main} ${phaseTags ? classes.phases : ""} ${hero ? classes.hero : ""} ${centered ? classes.centered : ""} ${mainClassName ?? ""}`}
      >
        {phaseTags && <Box className={classes.phaseRow}>{phaseTags}</Box>}
        {title && (
          <Typography component="h1" className={classes.heading}>
            {title}
          </Typography>
        )}
        {description && (
          <Typography
            className={`${classes.description} ${descriptionSize === "small" ? classes.smallDescription : ""}`}
          >
            {description}
          </Typography>
        )}
        <Box className={`${title ? classes.body : ""} ${bodyClassName ?? ""}`}>{children}</Box>
      </Box>
    </Box>
  );
};

export const RegistrationActions: React.FC<{
  onNext: () => void;
  onBack?: () => void;
  label?: string;
  disabled?: boolean;
  tone?: "primary" | "trust";
}> = ({ onNext, onBack, label = "次へ", disabled, tone = "primary" }) => {
  const classes = useStyles();
  return (
    <Box className={classes.actions}>
      <CustomButton buttonSize="L" type="button" tone={tone} onClick={onNext} disabled={disabled}>
        {label}
      </CustomButton>
      {onBack && (
        <Box className={classes.back}>
          <CustomButton type="button" tone="text" onClick={onBack}>
            戻る
          </CustomButton>
        </Box>
      )}
    </Box>
  );
};
